<template>
  <div class="medicines-chart-card">
    <div class="card-header">
      <div class="header-content">
        <h3 class="card-title">
          <i class="fas fa-pills title-icon"></i>
          10 Medicamentos Mais Utilizados
        </h3>
        <p class="card-subtitle">Prescrições ativas dos residentes</p>
      </div>
    </div>

    <div class="card-content">
      <ErrorsMessages :errors="errors" />

      <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
        <!-- No Results State -->
        <div
          v-show="chartData.length == 1 && errors.length == 0"
          class="no-results"
        >
          <i class="fas fa-chart-bar no-results-icon"></i>
          <h4 class="no-results-title">Sem resultados</h4>
          <p class="no-results-text">
            Não há dados de medicamentos para exibir no momento
          </p>
        </div>

        <!-- Charts Section -->
        <div v-show="chartData.length > 1" class="charts-section">
          <!-- Quantity Chart -->
          <div class="chart-container">
            <div class="chart-header">
              <h4 class="chart-title">
                <i class="fas fa-chart-bar mr-2"></i>
                Gráfico em Quantidade
              </h4>
            </div>
            <div class="chart-wrapper">
              <GChart
                :settings="{ packages: ['bar'] }"
                :data="chartData"
                :options="quantityChartOptions"
                :createChart="(el, google) => new google.charts.Bar(el)"
              />
            </div>
          </div>

          <!-- Divider -->
          <div class="chart-divider">
            <div class="divider-line"></div>
          </div>

          <!-- Percentage Chart -->
          <div class="chart-container">
            <div class="chart-header">
              <h4 class="chart-title">
                <i class="fas fa-chart-pie mr-2"></i>
                Gráfico em Porcentagem
              </h4>
            </div>
            <div class="chart-wrapper">
              <GChart
                :settings="{ packages: ['corechart'] }"
                type="PieChart"
                :data="chartDataPercentage"
                :options="percentageChartOptions"
              />
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import { GChart } from "vue-google-charts";
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";

export default {
  name: "MostUsedMedicines",
  components: {
    GChart,
    ErrorsMessages,
  },
  data() {
    return {
      chartsLib: null,
      chartData: [["MEDICAMENTOS", "QUANTIDADE"]],
      chartDataPercentage: [["MEDICAMENTOS", "QUANTIDADE"]],
      errors: [],
      loading: false,
    };
  },
  computed: {
    quantityChartOptions() {
      return {
        bars: "horizontal",
        colors: ["#0f65b0"],
        legend: { position: "none" },
        backgroundColor: "transparent",
        chartArea: {
          left: 200,
          top: 20,
          width: "70%",
          height: "85%",
        },
        hAxis: {
          textStyle: {
            color: "#64748b",
            fontSize: 12,
          },
          gridlines: {
            color: "#e5e7eb",
          },
        },
        vAxis: {
          textStyle: {
            color: "#374151",
            fontSize: 12,
            fontName: "WorkSans-Regular",
          },
        },
        height: 400,
      };
    },
    percentageChartOptions() {
      return {
        pieHole: 0.4,
        height: 400,
        backgroundColor: "transparent",
        colors: [
          "#0f65b0",
          "#1e40af",
          "#3b82f6",
          "#60a5fa",
          "#93c5fd",
          "#dbeafe",
          "#1d4ed8",
          "#2563eb",
          "#3730a3",
          "#4338ca",
        ],
        legend: {
          position: "right",
          alignment: "center",
          textStyle: {
            color: "#374151",
            fontSize: 12,
            fontName: "WorkSans-Regular",
          },
        },
        chartArea: {
          left: 20,
          top: 20,
          width: "75%",
          height: "85%",
        },
        pieSliceTextStyle: {
          color: "white",
          fontSize: 11,
          fontName: "WorkSans-SemiBold",
        },
      };
    },
  },
  created() {
    this.retrieve();
  },
  methods: {
    async retrieve() {
      try {
        this.loading = true;

        this.chartData = [["MEDICAMENTOS", "QUANTIDADE"]];
        this.chartDataPercentage = [["MEDICAMENTOS", "QUANTIDADE"]];

        const response = await api.get(
          "/rest-home/graphics/most-used-medicines"
        );

        const { medicines, error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          for (let medicine of medicines) {
            this.chartData.push([
              medicine.name,
              `${medicine.total} ${medicine.unit_of_measurement_abbreviation}`,
            ]);
            this.chartDataPercentage.push([medicine.name, medicine.total]);
          }
          this.errors = [];
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.medicines-chart-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  padding: 24px;
  border-bottom: 1px solid #0891b2;
}

.header-content {
  text-align: center;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0284c7;
  margin-right: 8px;
  font-size: 18px;
}

.card-subtitle {
  color: #075985;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.card-content {
  padding: 32px 24px;
}

.loading-overlay {
  border-radius: 12px;
  min-height: 300px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 60px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Charts Section */
.charts-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.chart-container {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.chart-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.chart-title i {
  color: #0f65b0;
}

.chart-wrapper {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Chart Divider */
.chart-divider {
  display: flex;
  align-items: center;
  margin: 16px 0;
}

.divider-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #e5e7eb 50%,
    transparent 100%
  );
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-content {
    padding: 20px 16px;
  }

  .card-header {
    padding: 20px 16px;
  }

  .card-title {
    font-size: 18px;
    flex-direction: column;
    gap: 4px;
  }

  .card-subtitle {
    font-size: 13px;
  }

  .chart-container {
    padding: 16px;
  }

  .chart-wrapper {
    padding: 12px;
  }

  .no-results {
    padding: 40px 16px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 18px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .charts-section {
    gap: 20px;
  }

  .chart-title {
    font-size: 14px;
  }

  .title-icon {
    font-size: 16px;
  }
}

/* Custom Chart Styling */
:deep(.google-visualization-charts-chart) {
  font-family: "WorkSans-Regular", sans-serif !important;
}
</style>